// Manual Quantity Calculator — pure scope state + validation helpers.
//
// The Countertops/Cabinets manual quantity calculator lets the estimator enter scopes by hand
// (product structure + QTY + unit price). NO graphical/floorplan/geometry math lives here — it
// is entirely manual entry. Kit quantity propagation is intentionally NOT implemented in this
// phase. These helpers are the pure, unit-tested core the React form consumes.

const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";
const toNumber = (v) => Number(String(v).trim());

// A blank scope row. `id` is supplied by the caller (the component keeps a counter) so this
// stays pure and deterministic for tests.
//
// A scope selects a Product Structure (category) and then a Master Product within it. When that
// master product belongs to a kit, `kitId`/`kitLines` are populated (see manualQuantityKits.js).
export function createEmptyScope(id) {
  return {
    id,
    productStructureId: "",
    masterProductId: "",
    masterProductName: "",
    kitId: 0,
    qty: "",
    unitPrice: "",
    kitLines: [],
  };
}

export function addScope(scopes, scope) {
  return [...(Array.isArray(scopes) ? scopes : []), scope];
}

export function removeScope(scopes, id) {
  return (Array.isArray(scopes) ? scopes : []).filter((s) => s.id !== id);
}

export function updateScope(scopes, id, field, value) {
  return (Array.isArray(scopes) ? scopes : []).map((s) =>
    s.id === id ? { ...s, [field]: value } : s
  );
}

// Extended price = QTY x Unit Price, rounded to cents (the app's money convention:
// Math.round(x*100)/100, cf. BUSINESS_LOGIC §6.2 / estimateTable). Blank/invalid inputs -> 0.
// This is the manual-entry line math; it does not re-implement the domain pricing engine
// (GPM / tax / discount) which stays in the estimate/pricing-contract modules.
export function lineTotal(qty, unitPrice) {
  const q = toNumber(qty);
  const p = toNumber(unitPrice);
  if (Number.isNaN(q) || Number.isNaN(p)) return 0;
  return Math.round(q * p * 100) / 100;
}

// Master line extended price (QTY x Unit Price).
export function scopeLineTotal(scope = {}) {
  return lineTotal(scope.qty, scope.unitPrice);
}

// Scope total = master extended price + every kit line's extended price.
export function scopeTotal(scope = {}) {
  const kitLines = Array.isArray(scope.kitLines) ? scope.kitLines : [];
  const kitSum = kitLines.reduce((sum, line) => sum + lineTotal(line.quantity, line.unitPrice), 0);
  return Math.round((scopeLineTotal(scope) + kitSum) * 100) / 100;
}

// Estimate total across all scopes.
export function estimateTotal(scopes = []) {
  const list = Array.isArray(scopes) ? scopes : [];
  return Math.round(list.reduce((sum, scope) => sum + scopeTotal(scope), 0) * 100) / 100;
}

// Currency display formatting (2 dp), matching the app's `toFixed(2)` convention. Invalid -> "0.00".
export function formatCurrency(value) {
  const n = toNumber(value);
  return (Number.isNaN(n) ? 0 : n).toFixed(2);
}

// ---------------------------------------------------------------------------
// Material / Labor breakdown (mirrors OrderConfirmation.jsx's Material & Labor tables).
// Kit lines are flattened across scopes into two aggregated sections; each section rolls up
// Cost / Profit / GPM using the app's existing GPM convention: GPM = profit / price * 100.
// ---------------------------------------------------------------------------
const round2 = (n) => Math.round(n * 100) / 100;

// Line extended cost = QTY x Unit Cost, rounded to cents. Blank/invalid inputs -> 0.
export function lineCost(qty, unitCost) {
  const q = toNumber(qty);
  const c = toNumber(unitCost);
  if (Number.isNaN(q) || Number.isNaN(c)) return 0;
  return round2(q * c);
}

// Flatten kit lines of a given type across all scopes into breakdown rows, keeping a back-ref
// to the owning scope + line so edits can be routed back.
function flattenRows(scopes, type) {
  return (Array.isArray(scopes) ? scopes : []).flatMap((scope) =>
    (Array.isArray(scope?.kitLines) ? scope.kitLines : [])
      .filter((line) => line.type === type)
      .map((line) => ({
        scopeId: scope.id,
        lineId: line.id,
        name: line.name || "",
        unit: line.unit || "",
        style: line.style || "",
        color: line.color || "",
        laborCategory: line.laborCategory || "",
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        unitCost: line.unitCost,
      }))
  );
}

// Material rows across every scope (kit lines flagged material).
export function materialRows(scopes = []) {
  return flattenRows(scopes, "material");
}

// Labor rows across every scope (kit lines flagged labor).
export function laborRows(scopes = []) {
  return flattenRows(scopes, "labor");
}

// Roll up a section's rows into { unitPriceTotal, linePriceTotal, cost, profit, gpm }.
// GPM is zero-safe (returns 0 when the section has no price).
export function sectionTotals(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  const unitPriceTotal = round2(
    list.reduce((sum, r) => sum + (Number.isNaN(toNumber(r.unitPrice)) ? 0 : toNumber(r.unitPrice)), 0)
  );
  const linePriceTotal = round2(list.reduce((sum, r) => sum + lineTotal(r.quantity, r.unitPrice), 0));
  const cost = round2(list.reduce((sum, r) => sum + lineCost(r.quantity, r.unitCost), 0));
  const profit = round2(linePriceTotal - cost);
  const gpm = linePriceTotal > 0 ? round2((profit / linePriceTotal) * 100) : 0;
  return { unitPriceTotal, linePriceTotal, cost, profit, gpm };
}

// Validate a single scope. Returns an errors object ({} when valid).
export function validateScope(scope = {}) {
  const errors = {};
  if (isBlank(scope.productStructureId)) {
    errors.productStructureId = "Product Structure is required";
  }
  if (isBlank(scope.qty)) {
    errors.qty = "QTY is required";
  } else if (Number.isNaN(toNumber(scope.qty)) || toNumber(scope.qty) <= 0) {
    errors.qty = "QTY must be greater than 0";
  }
  if (isBlank(scope.unitPrice)) {
    errors.unitPrice = "Unit Price is required";
  } else if (Number.isNaN(toNumber(scope.unitPrice)) || toNumber(scope.unitPrice) < 0) {
    errors.unitPrice = "Unit Price must be 0 or more";
  }
  return errors;
}

// Validate the whole estimate. Returns { description, scopes: { [id]: fieldErrors }, isValid }.
export function validateEstimate(estimate = {}) {
  const { description, scopes = [] } = estimate;
  const result = { description: "", scopes: {} };

  if (isBlank(description)) {
    result.description = "Estimate Description is required";
  }

  (Array.isArray(scopes) ? scopes : []).forEach((scope) => {
    const scopeErrors = validateScope(scope);
    if (Object.keys(scopeErrors).length > 0) {
      result.scopes[scope.id] = scopeErrors;
    }
  });

  result.isValid = !result.description && Object.keys(result.scopes).length === 0;
  return result;
}

// Groups materialList and laborList into ordered tab-cards by (scopeBlockId, structureName).
// Two items with the same structureName but different scopeBlockIds form separate groups,
// allowing multiple scope instances of the same type. Items without scopeBlockId fall back
// to structureName-only grouping for backward compatibility with old saved estimates.
// Each group carries absIdx (original flat-list index) so edit/delete handlers can operate
// on the flat list. Items with blank structureName and no scopeBlockId go into "Other".
export function groupByStructure(materialList, laborList) {
  const UNGROUPED = "__ungrouped__";
  const map = new Map();
  const order = [];

  const blockKey = (item) => {
    const num = item.scopeBlockId != null ? Number(item.scopeBlockId) : null;
    const sname =
      item.structureName && String(item.structureName).trim()
        ? String(item.structureName).trim()
        : UNGROUPED;
    // A scope BLOCK (positive scopeBlockId) groups ALL its rows into one card, even across product
    // structures — "add material/labor" rows belong to the scope block, not their own structure.
    // Legacy rows (block 0 / no block) still group per structure, unchanged.
    if (num != null && num > 0) return `block_${num}`;
    return num != null ? `${num}__${sname}` : sname;
  };

  const getGroup = (key, item) => {
    if (!map.has(key)) {
      const sname =
        item.structureName && String(item.structureName).trim()
          ? String(item.structureName).trim()
          : "Other";
      map.set(key, {
        scopeBlockId: item.scopeBlockId ?? null,
        structureName: key === UNGROUPED ? "Other" : sname,
        scopeName: item.scopeName || "",
        roomName: item.roomName || "",
        photoUrl: item.photoUrl || "",
        material: [],
        labor: [],
      });
      order.push(key);
    }
    return map.get(key);
  };

  (Array.isArray(materialList) ? materialList : []).forEach((item, absIdx) => {
    getGroup(blockKey(item), item).material.push({ ...item, absIdx });
  });

  (Array.isArray(laborList) ? laborList : []).forEach((item, absIdx) => {
    getGroup(blockKey(item), item).labor.push({ ...item, absIdx });
  });

  const named = order.filter((k) => k !== UNGROUPED).map((k) => map.get(k));
  return map.has(UNGROUPED) ? [...named, map.get(UNGROUPED)] : named;
}
